import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Ticket, Link2, CheckCircle2, Building, Loader2, Trash2, RefreshCw, ExternalLink, Search } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function ZohoDeskAdmin() {
  const [searchParams] = useSearchParams();
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [links, setLinks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [linkingAccount, setLinkingAccount] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter accounts by search
  const filteredAccounts = useMemo(() => {
    if (!searchQuery.trim()) return accounts;
    const query = searchQuery.toLowerCase();
    return accounts.filter(a => 
      (a.accountName || '').toLowerCase().includes(query) ||
      (a.email || '').toLowerCase().includes(query)
    );
  }, [accounts, searchQuery]);

  useEffect(() => {
    if (searchParams.get('zoho_connected') === 'true') {
      toast.success('Zoho connected successfully!');
    }
    checkStatus();
    fetchCompanies();
    fetchLinks();
  }, [searchParams]);

  const checkStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/zoho-desk/status`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setConnected(data.connected);
        if (data.connected) {
          fetchAccounts();
        }
      }
    } catch (error) {
      console.error('Failed to check status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/zoho-desk/accounts`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAccounts(data.accounts || []);
      }
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/companies`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };

  const fetchLinks = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/zoho-desk/links`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setLinks(data.links || []);
      }
    } catch (error) {
      console.error('Failed to fetch links:', error);
    }
  };

  const connectZoho = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/zoho/auth-url`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.auth_url;
      }
    } catch (error) {
      toast.error('Failed to connect to Zoho');
    }
  };

  const handleLinkAccount = async (account) => {
    if (!selectedCompany) {
      toast.error('Please select a company');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/zoho-desk/link-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          company_id: selectedCompany,
          zoho_account_id: String(account.id),
          zoho_account_name: account.accountName
        })
      });

      if (response.ok) {
        toast.success('Account linked successfully');
        setLinkingAccount(null);
        setSelectedCompany('');
        fetchLinks();
        fetchAccounts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to link account');
      }
    } catch (error) {
      toast.error('Failed to link account');
    }
  };

  const handleRemoveLink = async (linkId) => {
    if (!window.confirm('Are you sure you want to remove this link?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/zoho-desk/links/${linkId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Link removed');
        fetchLinks();
        fetchAccounts();
      }
    } catch (error) {
      toast.error('Failed to remove link');
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Support (Zoho Desk)">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Support (Zoho Desk)">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Ticket className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-mono font-bold mb-2">Zoho Desk Integration</h2>
                <p className="text-sm text-muted-foreground">
                  Link Zoho Desk accounts to your portal companies so customers can view their support tickets.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {connected ? (
                <span className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-mono">
                  <CheckCircle2 className="h-4 w-4" />
                  Connected
                </span>
              ) : (
                <Button onClick={connectZoho} className="bg-primary text-primary-foreground">
                  <Link2 className="h-4 w-4 mr-2" />
                  Connect Zoho
                </Button>
              )}
            </div>
          </div>
        </div>

        {!connected ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <Link2 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-mono font-bold mb-2">Connect Zoho Desk</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Connect your Zoho account to enable support ticket integration. This uses the same connection as Zoho Projects.
            </p>
            <Button onClick={connectZoho} className="bg-primary text-primary-foreground">
              <ExternalLink className="h-4 w-4 mr-2" />
              Connect to Zoho
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Zoho Desk Accounts */}
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <h3 className="text-lg font-mono font-bold">Zoho Desk Accounts</h3>
                <Button variant="outline" size="sm" onClick={fetchAccounts}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>

              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search accounts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              <div className="bg-card border border-border rounded-lg divide-y divide-border max-h-[500px] overflow-y-auto">
                {loadingAccounts ? (
                  <div className="p-8 text-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
                  </div>
                ) : filteredAccounts.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    {searchQuery ? 'No matching accounts found' : 'No accounts found in Zoho Desk'}
                  </div>
                ) : (
                  filteredAccounts.map((account) => (
                    <div key={account.id} className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-mono font-bold truncate">{account.accountName}</h4>
                          {account.email && (
                            <p className="text-xs text-muted-foreground">{account.email}</p>
                          )}
                          <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-mono ${
                            account.is_linked 
                              ? 'bg-primary/10 text-primary' 
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {account.is_linked ? 'Linked' : 'Not Linked'}
                          </span>
                        </div>
                        {!account.is_linked && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setLinkingAccount(account)}
                          >
                            Link
                          </Button>
                        )}
                      </div>

                      {/* Link Form */}
                      {linkingAccount?.id === account.id && (
                        <div className="mt-4 p-4 bg-background rounded-md border border-border">
                          <Label className="font-mono text-sm">Link to Company</Label>
                          <select
                            value={selectedCompany}
                            onChange={(e) => setSelectedCompany(e.target.value)}
                            className="w-full mt-1 px-3 py-2 bg-card border border-border rounded-md font-mono text-sm"
                          >
                            <option value="">Select a company...</option>
                            {companies.map((company) => (
                              <option key={company.company_id} value={company.company_id}>
                                {company.name}
                              </option>
                            ))}
                          </select>
                          <div className="flex gap-2 mt-3">
                            <Button 
                              size="sm" 
                              onClick={() => handleLinkAccount(account)}
                              disabled={!selectedCompany}
                            >
                              Confirm
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setLinkingAccount(null);
                                setSelectedCompany('');
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Current Links */}
            <div className="space-y-4">
              <h3 className="text-lg font-mono font-bold">Account Links</h3>
              <div className="bg-card border border-border rounded-lg divide-y divide-border max-h-[600px] overflow-y-auto">
                {links.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Building className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No accounts linked yet</p>
                    <p className="text-sm mt-1">Link Zoho Desk accounts to your portal companies</p>
                  </div>
                ) : (
                  links.map((link) => (
                    <div key={link.link_id} className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-mono font-bold truncate">{link.zoho_account_name}</h4>
                          <div className="flex items-center gap-2 mt-2">
                            <Building className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm text-primary">{link.company_name}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Linked: {new Date(link.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleRemoveLink(link.link_id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default ZohoDeskAdmin;
